// viewmodels/ApplyJobViewModel.ts
import { useReducer, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { formApi } from '../api/applyFormApi';
import { Job } from '../../../company/JobManagement/model/JobModel';
import { ToastMessage, initialState,applyFormReducer} from '../model/ApplicationModel';
import { validatePhone } from '../../../../utils/validation';

export const useApplyJobViewModel = (job: Job | null, navigate: ReturnType<typeof useNavigate>) => {
  const [state, dispatch] = useReducer(applyFormReducer, initialState);

  // Toast functions using dispatch
  const addToast = useCallback((message: string, type: ToastMessage['type']) => {
    dispatch({ type: 'ADD_TOAST', payload: { message, type } });

    setTimeout(() => {
      dispatch({ type: 'REMOVE_TOAST', payload: Date.now() });
    }, 5000);
  }, []);

  const removeToast = useCallback((id: number) => {
    dispatch({ type: 'REMOVE_TOAST', payload: id });
  }, []);

  // Fetch candidate profile
  const fetchCandidateProfile = useCallback(async () => {
    if (!job) {
      navigate('/candidate/jobs');
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING_PROFILE', payload: true });
      const profile = await formApi.getCandidateProfile();

      dispatch({ type: 'SET_CANDIDATE_PROFILE', payload: profile });
      
      dispatch({
        type: 'SET_FORM_DATA',
        payload: {
          firstName: profile.first_name || '',
          lastName: profile.last_name || '',
          email: profile.email || '',
          phone: profile.phone || '',
          yearsOfExperience: profile.years_of_experience || 0,
          lastDesignation: profile.current_job_title || '',
          expectedSalary: profile.expected_salary?.toString() || '',
        }
      });
    } catch (error: any) {
      console.error('Error fetching candidate profile:', error);
      dispatch({
        type: 'SET_PROFILE_ERROR',
        payload: 'Failed to load profile data. Please refresh and try again.'
      });
    } finally {
      dispatch({ type: 'SET_LOADING_PROFILE', payload: false });
    }
  }, [job, navigate]);

  useEffect(() => {
    fetchCandidateProfile();
  }, [fetchCandidateProfile]);

  // Form field change handler
  const handleFieldChange = useCallback((name: string, value: string | number) => {
    dispatch({ type: 'SET_FORM_FIELD', field: name, value });

    // Clear error for this field
    if (state.errors[name]) {
      dispatch({ type: 'CLEAR_FIELD_ERROR', field: name });
    }
  }, [state.errors]);

  // Validate form using validatePhone from utils
  const validateForm = useCallback((): Record<string, string> => {
    const newErrors: Record<string, string> = {};

    // Use validatePhone from utils
    const phoneValidation = validatePhone(state.formData.phone, true);
    
    if (!phoneValidation.isValid) {
      newErrors.phone = phoneValidation.message;
    }

    return newErrors;
  }, [state.formData.phone]);

  // Real-time field validation for onBlur events
  const validateField = useCallback((name: string, value: string): string => {
    let errorMessage = '';

    switch (name) {
      case 'phone':
        const phoneValidation = validatePhone(value, true);
        if (!phoneValidation.isValid) {
          errorMessage = phoneValidation.message;
        }
        break;
      // Add other field validations here as needed
      default:
        break;
    }

    return errorMessage;
  }, []);

  // Handle field blur for real-time validation
  const handleFieldBlur = useCallback((name: string, value: string) => {
    const errorMessage = validateField(name, value);
    
    dispatch({ 
      type: 'SET_ERRORS', 
      payload: { ...state.errors, [name]: errorMessage }
    });
  }, [state.errors, validateField]);

  // Submit application
  const submitApplication = useCallback(async () => {
    if (!job?.id) {
      addToast('Invalid job ID', 'error');
      return;
    }

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      dispatch({ type: 'SET_ERRORS', payload: validationErrors });
      return;
    }

    dispatch({ type: 'SET_SUBMIT_LOADING', payload: true });

    try {
      const applicationData = {
        firstName: state.formData.firstName,
        lastName: state.formData.lastName,
        email: state.formData.email,
        phone: state.formData.phone,
        yearsOfExperience: state.formData.yearsOfExperience,
        lastDesignation: state.formData.lastDesignation,
        expectedSalary: state.formData.expectedSalary,
        coverLetter: state.formData.coverLetter,
        additionalInfo: state.formData.additionalInfo
      };

      const response = await formApi.apply(job.id, applicationData);

      addToast('Application submitted successfully!', 'success');
      dispatch({ type: 'SET_SUBMIT_SUCCESS', payload: true });

      setTimeout(() => {
        navigate('/candidate/jobs');
      }, 2000);

    } catch (error: any) {
      console.error('❌ Application error:', error);

      let errorMessage = 'Failed to submit application. Please try again.';
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (typeof error?.response?.data === 'string') {
        errorMessage = error.response.data;
      }

      addToast(errorMessage, 'error');
      dispatch({
        type: 'SET_ERRORS',
        payload: { submit: errorMessage }
      });
    } finally {
      dispatch({ type: 'SET_SUBMIT_LOADING', payload: false });
    }
  }, [job?.id, state.formData, validateForm, addToast, navigate]);

  // Confirm application
  const confirmApplication = useCallback(() => {
    dispatch({ type: 'SET_SHOW_CONFIRM_MODAL', payload: false });
    submitApplication();
  }, [submitApplication]);

  // Toggle confirm modal
  const toggleConfirmModal = useCallback((show: boolean) => {
    dispatch({ type: 'SET_SHOW_CONFIRM_MODAL', payload: show });
  }, []);

  // Reset form
  const resetForm = useCallback(() => {
    dispatch({ type: 'RESET_FORM' });
  }, []);

  // Check if form is valid
  const isFormValid = useCallback((): boolean => {
    const errors = validateForm();
    return Object.keys(errors).length === 0;
  }, [validateForm]);

  return {
    formData: state.formData,
    loading: state.loading,
    loadingProfile: state.loadingProfile,
    submitSuccess: state.submitSuccess,
    showConfirmModal: state.showConfirmModal,
    errors: state.errors,
    profileError: state.profileError,
    candidateProfile: state.candidateProfile,
    toasts: state.toasts,
    handleFieldChange,
    handleFieldBlur,
    submitApplication,
    confirmApplication,
    toggleConfirmModal,
    removeToast,
    addToast,
    resetForm,
    isFormValid,
    validateField
  };
};